import { useState, useEffect, useCallback } from "react";
import { EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { EntityManagerOtherParams, otherParamsSchema } from "@/react/schema/entityManagerSchema";
import { useHistory } from "react-router-dom";

const EVENT_NAME = "entityManager:urlChanged";

interface LocationState {
  isDashboard?: boolean;
  [key: string]: unknown;
}

const getQueryParams = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const entityType = params.get("entityType") || "";
  const entityId = params.get("entityId") || "";
  const otherParamsRaw = params.get("otherParams");
  let otherParams: EntityManagerOtherParams | null = null;

  if (otherParamsRaw) {
    try {
      const parsed = JSON.parse(otherParamsRaw);
      otherParams = otherParamsSchema.parse(parsed);
    } catch {
      otherParams = null;
    }
  }

  return { entityType, entityId, otherParams };
};

const setQueryParams = (
  newParams: Record<string, unknown>,
  history: ReturnType<typeof useHistory>
) => {
  const url = new URL(window.location.href);
  Object.entries(newParams).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      url.searchParams.delete(key);
    } else if (typeof value === "object") {
      url.searchParams.set(key, JSON.stringify(value));
    } else {
      url.searchParams.set(key, String(value));
    }
  });

  const locationState = history?.location?.state as LocationState | undefined;

  if (locationState?.isDashboard) {
    history.replace({
      pathname: url.pathname,
      search: url.search,
      state: {
        ...locationState,
        isDashboard: true
      }
    });
  } else {
    history.push({
      pathname: url.pathname,
      search: url.search,
      state: {
        ...locationState,
        isDashboard: true
      }
    });
  }


  window.dispatchEvent(new Event(EVENT_NAME));
};

export const useEntityManager = () => {
  const history = useHistory();
  const [state, setState] = useState(() => {
    const params = getQueryParams();
    return params;
  });
  const isOpen = Boolean(state.entityType) && Boolean(state.entityId);

  useEffect(() => {
    const sync = () => setState(getQueryParams());
    window.addEventListener("popstate", sync);
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, []);

  const updateState = useCallback(
    (newPartial: Record<string, unknown>) => {
      const next = { ...state, ...newPartial };
      setQueryParams(next, history);
      setState(next);
    },
    [state, history]
  );

  const openEntityManager = useCallback(
    ({ entityType, entityId, otherParams }: { entityType: EntityTypeKey; entityId: string | number; otherParams?: EntityManagerOtherParams }) => {
      updateState({
        entityType,
        entityId: String(entityId),
        otherParams: otherParams && Object.values(otherParams).length > 0 ? otherParams : null
      });
    },
    [updateState]
  );

  const closeEntityManager = useCallback(() => {
    history.goBack();
  }, [history]);

  const forceCloseEntityManager = () => {
    if (isOpen) {
      const url = new URL(window.location.href);
      url.searchParams.delete("entityType");
      url.searchParams.delete("entityId");
      url.searchParams.delete("otherParams");
      history.replace({
        pathname: url.pathname,
        search: url.search,
        state: history.location.state
      });
      setState({});
    }
  }

  return {
    entityType: state.entityType || null,
    entityId: state.entityId || null,
    otherParams: state.otherParams ?? null,
    openEntityManager,
    closeEntityManager,
    isOpen,
    forceCloseEntityManager
  };
};
